import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useVoiceFeedback } from '../hooks/useVoiceFeedback';
import { ExportButton } from './ExportButton';
import {
  FaPen,
  FaEraser,
  FaHighlighter,
  FaEye,
  FaUndo,
  FaTrash,
  FaCog,
  FaPalette,
  FaChevronDown,
  FaCircle,
  FaPlay,
  FaArrowRight,
  FaMinus,
  FaHandPointer,
  FaTimes,
  FaBars,
  FaExpandAlt,
  FaMoon,
  FaSun,
  FaMicrophone,
  FaStop,
  FaList,
  FaSave,
  FaShare,
  FaVectorSquare,
  FaStar,
  FaCube,
  FaShapes,
  FaEllipsisH,
  FaSpinner,
  FaVideo
} from 'react-icons/fa';
import { BsHexagon, BsPentagon, BsTriangle, BsSquare, BsCircle, BsDiamond } from 'react-icons/bs';
import { TbCylinder, TbPyramid } from 'react-icons/tb';
import { BiGridAlt } from 'react-icons/bi';
import { VscGraphLine } from 'react-icons/vsc';
import { useWhiteboardStore, toolSettings } from '../store/useWhiteboardStore';
import { useUserStore } from '../store/useUserStore';
import { useTenantStore } from '../store/useTenantStore';
import { Tool, Recording } from '../types';
import { useRecordingInit } from '../hooks/useRecordingInit';
import { useNavigate } from 'react-router-dom';
import { generateDeviceId } from '../utils/deviceInfo';
import { devicePreferencesService } from '../lib/firebase/devicePreferencesService';
import { useTheme } from '../hooks/useDarkMode';
import { SolutionViewerDropdown } from './SolutionViewerDropdown';
import { RecordingService, RecordingMetadata } from '../lib/firebase/recordingService';
import { RecordingPermissions } from '../types/whiteboardProps';
import { getEffectiveRecordingPermissions } from '../utils/recordingPermissionUtils';
import { getGlassClasses, GlassColor } from '../utils/glassStyles';
import QuestionListPanel from './QuestionListPanel';

type Position = 'top' | 'bottom' | 'left' | 'right';
type Theme = 'light' | 'dark';

interface ToolbarConfig {
  position: Position;
  theme: Theme;
  isCollapsed: boolean;
  isCompact: boolean;
  isDragging?: boolean;
  customPosition?: { x: number; y: number };
  customPositionRatio?: { xRatio: number; yRatio: number };
}

interface ModernToolbarProps {
  allowExport?: boolean;
  // New recording permissions system
  recordingPermissions?: RecordingPermissions;
  // Deprecated recording props (kept for backward compatibility)
  showRecordingControls?: boolean;
  allowRecording?: boolean;
  showRecordingsList?: boolean;
  maxRecordingDuration?: number;
  recordingQuality?: 'low' | 'medium' | 'high';
  autoSaveRecording?: boolean;
  currentQuestionId?: string;
  currentTestId?: string;
  // Solution viewing props
  allowSolutionViewing?: boolean;
  solutionVisibilityMode?: 'own' | 'tenant' | 'test' | 'public';
  allowOwnSolutionManagement?: boolean;
  maxSolutionsToShow?: number;
  showSolutionAuthorInfo?: boolean;
  showSolutionStats?: boolean;
  syncState?: any;
  // Video props
  hasVideo?: boolean;
  onVideoClick?: () => void;
  // Elimination display
  showEliminationDisplay?: boolean;
  onToggleEliminationDisplay?: () => void;
  hasEliminatedChoices?: boolean;
  // Tablet navigation props
  isTablet?: boolean;
  showTabletQuestionNav?: boolean;
  onToggleTabletQuestionNav?: () => void;
  hasQuestions?: boolean;
  currentQuestionIndex?: number;
  totalQuestions?: number;
  // Additional metadata for recording naming
  testTitle?: string;
  qbGroups?: any[];
  currentQbQuestion?: any;
  // Optical mode and question navigation
  isOpticalMode?: boolean;
  questions?: any[];
  onQuestionNavigate?: (index: number) => void;
}

const ModernToolbar: React.FC<ModernToolbarProps> = ({ 
  allowExport = false,
  recordingPermissions,
  showRecordingControls = true,
  allowRecording = true,
  showRecordingsList = true,
  maxRecordingDuration,
  recordingQuality = 'medium',
  autoSaveRecording = false,
  currentQuestionId,
  currentTestId,
  // Solution viewing props
  allowSolutionViewing = false,
  solutionVisibilityMode = 'own',
  allowOwnSolutionManagement = true,
  maxSolutionsToShow = 10,
  showSolutionAuthorInfo = true,
  showSolutionStats = true,
  hasVideo = false,
  onVideoClick,
  // Elimination display
  showEliminationDisplay = true,
  onToggleEliminationDisplay,
  hasEliminatedChoices = false,
  // Tablet navigation props
  isTablet = false,
  showTabletQuestionNav = false,
  onToggleTabletQuestionNav,
  hasQuestions = false,
  currentQuestionIndex = 0,
  totalQuestions = 0,
  // Additional metadata for recording naming
  testTitle,
  qbGroups,
  currentQbQuestion,
  // Optical mode and question navigation
  isOpticalMode = false,
  questions = [],
  onQuestionNavigate
}) => {
  // Get effective recording permissions
  const effectivePermissions = getEffectiveRecordingPermissions(
    recordingPermissions,
    {
      showRecordingControls,
      allowRecording,
      showRecordingsList,
      maxRecordingDuration,
      recordingQuality,
      autoSaveRecording
    }
  );
  

  
  const { isDark } = useTheme();
  const [config, setConfig] = useState<ToolbarConfig>({
    position: 'bottom',
    theme: isDark ? 'dark' : 'light',
    isCollapsed: false,
    isCompact: true
  });
  
  const [showSettings, setShowSettings] = useState(false);
  const [showShapeDropdown, setShowShapeDropdown] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showPositionMenu, setShowPositionMenu] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [visibleItems, setVisibleItems] = useState<number>(999);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const toolbarContentRef = useRef<HTMLDivElement>(null);
  const dragStartRef = useRef<{ x: number; y: number } | null>(null);
  const toolbarStartRef = useRef<{ x: number; y: number } | null>(null);
  
  // Recording states
  const [showRecordings, setShowRecordings] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [recordingName, setRecordingName] = useState('');
  const navigate = useNavigate();
  
  // Initialize recordings from localStorage
  const localRecordings = useRecordingInit();
  const [firebaseRecordings, setFirebaseRecordings] = useState<RecordingMetadata[]>([]);
  const [loadingRecordings, setLoadingRecordings] = useState(false);
  const [questionsWithRecordingsCount, setQuestionsWithRecordingsCount] = useState(0);
  const [showQuestionList, setShowQuestionList] = useState(false);
  
  // Get user and tenant info
  const userInfo = useUserStore((state) => state.userInfo);
  const tenantId = useTenantStore((state) => state.tenantId);
  
  // Voice feedback disabled
  // const { speakToolChange } = useVoiceFeedback();
  
  const {
    currentTool,
    currentColor,
    currentWidth,
    isPenMode,
    touchEnabled,
    penSettings,
    shapeRecognitionEnabled,
    showAnswerFeedback,
    glassEffects,
    devicePreferences,
    setCurrentTool,
    setCurrentColor,
    setCurrentWidth,
    clearCanvas,
    undoLastStroke,
    setPenMode,
    setTouchEnabled,
    updatePenSettings,
    setShapeRecognitionEnabled,
    setShowAnswerFeedback,
    setGlassEffects,
    // Recording functions
    recordingSession,
    startRecording,
    stopRecording,
    saveRecording,
    deleteRecording,
    saveDevicePreferences,
    updateDevicePreferences,
    questionPositionMetadata,
    // Canvas reference
    canvasRef
  } = useWhiteboardStore();


  // Recording handlers
  const handleStartRecording = async () => {
    await startRecording(currentQuestionId, currentTestId);
  };

  // Helper function to find which group a question belongs to
  const findQuestionGroup = (questionId: string | undefined) => {
    if (!questionId || !qbGroups || qbGroups.length === 0) return null;
    
    for (const group of qbGroups) {
      if (group.courses) {
        for (const course of group.courses) {
          if (course.questions) {
            for (const question of course.questions) {
              if (question.questionId === questionId) {
                return {
                  groupName: group.groupName || group.groupId,
                  courseName: course.courseName || course.courseId,
                  questionOrder: question.questionOrder || question.questionNumber
                };
              }
            }
          }
        }
      }
    }
    return null;
  };

  // Generate a smart recording name based on test and question metadata
  const generateRecordingName = () => {
    const parts = [];
    
    // Add test title if available
    if (testTitle) {
      // Truncate long test titles
      const truncatedTitle = testTitle.length > 30 ? testTitle.substring(0, 30) + '...' : testTitle;
      parts.push(truncatedTitle);
    }
    
    // Add group/course info if available
    if (currentQuestionId) {
      const groupInfo = findQuestionGroup(currentQuestionId);
      if (groupInfo) {
        if (groupInfo.courseName) {
          parts.push(groupInfo.courseName);
        }
        if (groupInfo.questionOrder) {
          parts.push(`Soru ${groupInfo.questionOrder}`);
        }
      } else if (currentQuestionIndex !== undefined && totalQuestions > 0) {
        // Fallback to question index
        parts.push(`Soru ${currentQuestionIndex + 1}/${totalQuestions}`);
      }
    }
    
    // Add timestamp
    const now = new Date();
    const timeStr = now.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
    const dateStr = now.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' });
    parts.push(`${dateStr} ${timeStr}`);
    
    return parts.filter(p => p).join(' - ');
  };

  const handleStopRecording = async () => {
    await stopRecording();
    setShowSaveDialog(true);
    setRecordingName(generateRecordingName());
  };

  const handleSaveRecording = () => {
    if (recordingName.trim()) {
      // Get current question metadata from store
      const currentMetadata = questionPositionMetadata[currentQuestionId || ''];
      
      saveRecording(recordingName.trim(), false, currentMetadata);
      setShowSaveDialog(false);
      setRecordingName('');
    }
  };

  const handlePlayRecording = (recording: Recording) => {
    navigate(`/player/${recording.id}`);
  };

  const handleShareRecording = (recording: Recording) => {
    const url = `${window.location.origin}/player/${recording.id}`;
    
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(() => {
        alert('Paylaşım linki kopyalandı!');
      });
    }
  };

  const formatDuration = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Load recording count when question changes
  useEffect(() => {
    if (currentQuestionId) {
      loadRecordingCount();
    }
  }, [currentQuestionId]);

  // Load Firebase recordings when modal opens
  // Commented out because we now use getAllRecordings when button is clicked
  // useEffect(() => {
  //   if (showRecordings && currentTestId && tenantId) {
  //     loadFirebaseRecordings();
  //   }
  // }, [showRecordings, currentTestId, tenantId]);

  const loadRecordingCount = async () => {
    if (!currentQuestionId) return;
    
    try {
      // Get user info
      const { user } = useUserStore.getState();
      const { currentTenant } = useTenantStore.getState();
      
      // Load all recordings same way as button click to get accurate count
      let recordings: RecordingMetadata[] = [];
      
      // First, get user's own recordings for this question
      if (user?.id) {
        const ownRecordings = await RecordingService.getRecordingsByQuestion(
          currentQuestionId,
          user.id.toString(),
          50
        );
        recordings = [...ownRecordings];
      }
      
      // Then, get public recordings for this question
      const publicRecordings = await RecordingService.getPublicRecordingsByQuestion(
        currentQuestionId,
        50
      );
      
      // Merge and deduplicate
      const recordingIds = new Set(recordings.map(r => r.id));
      publicRecordings.forEach(r => {
        if (!recordingIds.has(r.id)) {
          recordings.push(r);
        }
      });
      
      // If tenant mode, also get tenant recordings
      if (currentTenant?.tenantId) {
        const tenantRecordings = await RecordingService.getTenantRecordingsByQuestion(
          currentQuestionId,
          currentTenant.tenantId.toString(),
          50
        );
        
        tenantRecordings.forEach(r => {
          if (!recordingIds.has(r.id)) {
            recordings.push(r);
          }
        });
      }
      
      setQuestionsWithRecordingsCount(recordings.length);
    } catch (error) {
      console.error('Failed to load recording count:', error);
      setQuestionsWithRecordingsCount(0);
    }
  };

  const loadFirebaseRecordings = async () => {
    if (!currentTestId || !tenantId) return;
    
    try {
      setLoadingRecordings(true);
      // Use the same constant for blank whiteboard
      const testIdToUse = (!currentTestId || currentTestId === 'blank-whiteboard-00000000-0000-0000-0000-000000000000') 
        ? 'blank-whiteboard-00000000-0000-0000-0000-000000000000' 
        : currentTestId;
        
      const recordings = await RecordingService.getRecordingsByTest(
        testIdToUse,
        tenantId,
        undefined, // Get all recordings, not just for current user
        50 // Get more recordings
      );
      setFirebaseRecordings(recordings);
    } catch (error) {
      console.error('Failed to load Firebase recordings:', error);
    } finally {
      setLoadingRecordings(false);
    }
  };

  // Load toolbar preferences from Firebase
  const loadToolbarPreferences = async () => {
    try {
      // Get user context
      const tenantId = useTenantStore.getState().tenantId;
      const user = useUserStore.getState().userInfo;
      
      if (!tenantId || !user?.id) return;
      
      // Set user context for device preferences
      devicePreferencesService.setUserContext(tenantId, user.id);
      
      const preferences = await devicePreferencesService.loadPreferences();
      if (preferences?.preferences) {
        const prefs = preferences.preferences;
        
        // Apply toolbar preferences
        if (prefs.toolbarCompact !== undefined) {
          setConfig(prev => ({ ...prev, isCompact: prefs.toolbarCompact! }));
        }
        if (prefs.toolbarPosition) {
          setConfig(prev => ({ ...prev, position: prefs.toolbarPosition! }));
        }
        if (prefs.toolbarCustomPosition) {
          // Convert ratio-based position to pixel-based
          const xRatio = prefs.toolbarCustomPosition.xRatio;
          const yRatio = prefs.toolbarCustomPosition.yRatio;
          
          if (xRatio !== undefined && yRatio !== undefined) {
            const x = xRatio * window.innerWidth;
            const y = yRatio * window.innerHeight;
            
            setConfig(prev => ({ 
              ...prev, 
              customPosition: { x, y },
              customPositionRatio: { xRatio, yRatio }
            }));
          }
        }
      }
    } catch (error) {
    }
  };

  // Save toolbar preferences to Firebase
  const saveToolbarPreferences = useCallback(async () => {
    try {
      const preferencesToSave: any = {
        toolbarCompact: config.isCompact,
        toolbarPosition: config.position,
        defaultTool: currentTool,
        defaultColor: currentColor,
        defaultWidth: currentWidth,
        toolSettings: toolSettings // Save all tool-specific settings
      };
      
      // Only include customPosition if it exists, save as ratio
      if (config.customPositionRatio) {
        preferencesToSave.toolbarCustomPosition = config.customPositionRatio;
      } else if (config.customPosition) {
        // Fallback: calculate ratio from pixel position
        preferencesToSave.toolbarCustomPosition = {
          xRatio: config.customPosition.x / window.innerWidth,
          yRatio: config.customPosition.y / window.innerHeight
        };
      }
      
      await devicePreferencesService.updatePreferences(preferencesToSave);
    } catch (error) {
      // Silently fail if user is not authenticated
    }
  }, [config, currentTool, currentColor, currentWidth]);

  // Save config changes
  useEffect(() => {
    const timer = setTimeout(() => {
      saveToolbarPreferences();
    }, 1000); // Debounce saves

    return () => clearTimeout(timer);
  }, [config, saveToolbarPreferences]);

  // Load preferences on mount
  useEffect(() => {
    loadToolbarPreferences();
  }, []);

  // Handle window resize to recalculate custom position from ratio
  useEffect(() => {
    const handleResize = () => {
      if (config.customPositionRatio && !config.isDragging) {
        const x = config.customPositionRatio.xRatio * window.innerWidth;
        const y = config.customPositionRatio.yRatio * window.innerHeight;
        
        setConfig(prev => ({
          ...prev,
          customPosition: { x, y }
        }));
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [config.customPositionRatio, config.isDragging]);

  // Preference change handlers
  const handlePenModeChange = (enabled: boolean) => {
    setPenMode(enabled);
    saveDevicePreferences();
  };

  const handleTouchEnabledChange = (enabled: boolean) => {
    setTouchEnabled(enabled);
    saveDevicePreferences();
  };

  const handlePenSettingsChange = (settings: Partial<typeof penSettings>) => {
    updatePenSettings(settings);
    saveDevicePreferences();
  };

  const handleShapeRecognitionChange = (enabled: boolean) => {
    setShapeRecognitionEnabled(enabled);
    saveDevicePreferences();
  };

  const handleShowAnswerFeedbackChange = (show: boolean) => {
    setShowAnswerFeedback(show);
    saveDevicePreferences();
  };

  const handleGlassEffectsChange = (enabled: boolean) => {
    setGlassEffects(enabled);
    saveDevicePreferences();
  };

  const handleGlassColorChange = async (color: GlassColor) => {
    await updateDevicePreferences({ glassColor: color });
  };

  const handleQuestionFrameMarginsChange = async (margins: { top?: number; bottom?: number; left?: number; right?: number }) => {
    const currentMargins = devicePreferences?.preferences?.questionFrameMargins || {};
    const updatedMargins = { ...currentMargins, ...margins };
    await updateDevicePreferences({ questionFrameMargins: updatedMargins });
  };

  const handleToolChange = (tool: Tool) => {
    setCurrentTool(tool);
    // Voice feedback disabled
    // speakToolChange(tool);
    saveDevicePreferences();
  };

  const handleColorChange = (color: string) => {
    setCurrentColor(color);
    saveDevicePreferences();
  };

  const handleWidthChange = (width: number) => {
    setCurrentWidth(width);
    saveDevicePreferences();
  };

  // Load saved quick colors
  useEffect(() => {
    const savedQuickColors = localStorage.getItem('quickColors');
    if (savedQuickColors) {
      try {
        setQuickColors(JSON.parse(savedQuickColors));
      } catch (e) {
        console.error('Failed to parse quick colors from localStorage:', e);
      }
    }
  }, []);

  // Calculate overflow and determine visible items
  const checkOverflow = useCallback(() => {
    if (config.isCollapsed || !toolbarRef.current || !toolbarContentRef.current) return;

    const toolbar = toolbarRef.current;
    const content = toolbarContentRef.current;
    const isHorizontal = config.position === 'top' || config.position === 'bottom';
    
    // Get all toolbar sections (dividers and content groups)
    const sections = content.children;
    if (!sections.length) return;

    // Calculate available space more accurately
    const windowWidth = window.innerWidth;
    const isTablet = windowWidth >= 768 && windowWidth < 1024;
    const padding = config.isCompact ? 32 : (isMobile ? 20 : (isTablet ? 28 : 36));
    const moreButtonWidth = 60; // Space for "More" button
    const safeMargin = 20; // Extra margin for safety
    
    // For horizontal toolbars, ensure it fits within viewport
    let availableSpace;
    if (isHorizontal) {
      // Use viewport width minus safe margins
      const maxWidth = Math.min(windowWidth - 40, toolbar.clientWidth);
      availableSpace = maxWidth - padding - moreButtonWidth - safeMargin;
    } else {
      availableSpace = toolbar.clientHeight - padding - moreButtonWidth;
    }

    let currentSpace = 0;
    let itemsToShow = 0;
    
    // Check each section
    for (let i = 0; i < sections.length; i++) {
      const section = sections[i] as HTMLElement;
      const sectionSize = isHorizontal ? section.offsetWidth : section.offsetHeight;
      
      if (currentSpace + sectionSize > availableSpace) {
        // This section would overflow
        break;
      }
      
      currentSpace += sectionSize;
      itemsToShow++;
    }

    // Always show at least the first few essential items (collapse, tools, divider)
    // But be more aggressive on tablets
    const minItems = isTablet ? 6 : 8; // Increased to ensure recording section is visible
    setVisibleItems(Math.max(minItems, itemsToShow));
  }, [config.isCollapsed, config.position, config.isCompact, isMobile]);

  // Set up ResizeObserver for overflow detection
  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      checkOverflow();
    });

    if (toolbarRef.current) {
      resizeObserver.observe(toolbarRef.current);
    }

    // Initial check
    checkOverflow();

    return () => {
      resizeObserver.disconnect();
    };
  }, [checkOverflow]);

  // Recheck overflow when config changes
  useEffect(() => {
    checkOverflow();
  }, [config, checkOverflow]);

  // Detect mobile and tablet
  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      const isMobileDevice = width < 768;
      const isTablet = width >= 768 && width < 1024;
      
      setIsMobile(isMobileDevice);
      
      // Auto-compact on mobile and tablet
      if (isMobileDevice || isTablet) {
        setConfig(prev => ({ ...prev, isCompact: true }));
      }
    };
    
    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  // Drag handlers - Support mouse, touch, and pen
  const handleDragStart = (e: React.PointerEvent) => {
    if (isMobile) return;
    
    const toolbar = toolbarRef.current;
    if (!toolbar) return;
    
    // Capture pointer for this element
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    
    const rect = toolbar.getBoundingClientRect();
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    toolbarStartRef.current = { x: rect.left, y: rect.top };
    
    setConfig(prev => ({ ...prev, isDragging: true }));
  };

  const handleDragMove = useCallback((e: React.PointerEvent) => {
    if (!config.isDragging || !dragStartRef.current || !toolbarStartRef.current) return;
    
    const deltaX = e.clientX - dragStartRef.current.x;
    const deltaY = e.clientY - dragStartRef.current.y;
    
    const newX = toolbarStartRef.current.x + deltaX;
    const newY = toolbarStartRef.current.y + deltaY;
    
    // Ensure toolbar stays within viewport
    const toolbar = toolbarRef.current;
    if (toolbar) {
      const rect = toolbar.getBoundingClientRect();
      const maxX = window.innerWidth - rect.width;
      const maxY = window.innerHeight - rect.height;
      
      const finalX = Math.max(0, Math.min(maxX, newX));
      const finalY = Math.max(0, Math.min(maxY, newY));
      
      setConfig(prev => ({ 
        ...prev, 
        customPosition: { 
          x: finalX, 
          y: finalY 
        },
        customPositionRatio: {
          xRatio: finalX / window.innerWidth,
          yRatio: finalY / window.innerHeight
        },
        position: 'bottom' // Reset position when dragging
      }));
    }
  }, [config.isDragging]);

  const handleDragEnd = useCallback((e: React.PointerEvent) => {
    // Release pointer capture
    (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    
    setConfig(prev => ({ ...prev, isDragging: false }));
    dragStartRef.current = null;
    toolbarStartRef.current = null;
  }, []);

  // Add pointer event listeners to drag handle
  const dragHandleProps = {
    onPointerDown: handleDragStart,
    onPointerMove: config.isDragging ? handleDragMove : undefined,
    onPointerUp: config.isDragging ? handleDragEnd : undefined,
    onPointerCancel: config.isDragging ? handleDragEnd : undefined,
  };

  const tools: { tool: Tool; icon: React.ReactNode; label: string }[] = [
    { tool: 'select', icon: <FaHandPointer />, label: 'Seç' },
    { tool: 'pen', icon: <FaPen />, label: 'Kalem' },
    { tool: 'eraser', icon: <FaEraser />, label: 'Silgi' },
    { tool: 'object-eraser', icon: <FaEraser className="text-red-500" />, label: 'Nesne Sil' },
    { tool: 'area-eraser', icon: <FaVectorSquare />, label: 'Bölge Sil' },
    { tool: 'highlighter', icon: <FaHighlighter />, label: 'İşaretle' },
    { tool: 'laser', icon: <FaEye />, label: 'Lazer' },
  ];

  const shapeTools: { tool: Tool; icon: React.ReactNode; label: string; category?: string }[] = [
    // Basic 2D Shapes
    { tool: 'circle', icon: <BsCircle className="text-lg" />, label: 'Daire', category: '2D' },
    { tool: 'rectangle', icon: <BsSquare className="text-lg" />, label: 'Dikdörtgen', category: '2D' },
    { tool: 'triangle', icon: <BsTriangle className="text-lg" />, label: 'Üçgen', category: '2D' },
    { tool: 'line', icon: <FaMinus className="text-lg" />, label: 'Çizgi', category: '2D' },
    { tool: 'arrow', icon: <FaArrowRight className="text-lg" />, label: 'Ok', category: '2D' },
    { tool: 'star', icon: <FaStar className="text-lg" />, label: 'Yıldız', category: '2D' },
    { tool: 'pentagon', icon: <BsPentagon className="text-lg" />, label: 'Beşgen', category: '2D' },
    { tool: 'hexagon', icon: <BsHexagon className="text-lg" />, label: 'Altıgen', category: '2D' },
    { tool: 'ellipse', icon: <div className="w-5 h-3 border-2 border-current rounded-full" />, label: 'Elips', category: '2D' },
    { tool: 'diamond', icon: <BsDiamond className="text-lg" />, label: 'Elmas', category: '2D' },
    { tool: 'parallelogram', icon: <div className="w-5 h-3 border-2 border-current transform skew-x-12" />, label: 'Paralelkenar', category: '2D' },
    { tool: 'trapezoid', icon: <div className="w-0 h-0 border-l-[10px] border-r-[10px] border-b-[12px] border-l-transparent border-r-transparent border-b-current" />, label: 'Yamuk', category: '2D' },
    
    // 3D Shapes
    { tool: 'cube', icon: <FaCube className="text-lg" />, label: 'Küp', category: '3D' },
    { tool: 'cylinder', icon: <TbCylinder className="text-xl" />, label: 'Silindir', category: '3D' },
    { tool: 'pyramid', icon: <TbPyramid className="text-xl" />, label: 'Piramit', category: '3D' },
    
    // Special
    { tool: 'coordinate', icon: <VscGraphLine className="text-xl" />, label: 'Koordinat', category: 'Özel' },
    { tool: 'grid', icon: <BiGridAlt className="text-xl" />, label: 'Izgara', category: 'Özel' },
  ];

  const [quickColors, setQuickColors] = useState(['#000000', '#FF0000', '#0000FF', '#00FF00']);
  const [showColorCustomizer, setShowColorCustomizer] = useState(false);
  const [colorToReplace, setColorToReplace] = useState<number | null>(null);
  
  const colors = [
    '#000000', '#FFFFFF', '#FF0000', '#00FF00', '#0000FF', 
    '#FFFF00', '#FF00FF', '#00FFFF', '#FFA500', '#800080',
  ];

  const widths = [1, 2, 3, 4, 6, 8, 12, 16, 20, 24, 32, 48];

  // Position styles
  const getPositionStyles = () => {
    const base = "fixed z-50 transition-all duration-300";
    const glassColor = devicePreferences?.preferences?.glassColor || 'crystal';
    const bgTheme = config.theme === 'dark' 
      ? glassEffects !== false ? `${getGlassClasses(glassColor as GlassColor, true)} border` : 'bg-gray-900 border-gray-700'
      : glassEffects !== false ? `${getGlassClasses(glassColor as GlassColor, false)} border` : 'bg-white border-gray-200';
    const glassEffect = ''; // Glass effect is now included in getGlassClasses
    
    // If custom position is set (dragged), use that
    if (config.customPosition) {
      return `${base} ${bgTheme} ${glassEffect} rounded-2xl shadow-2xl border`;
    }
    
    // Add max-width constraint for horizontal toolbars
    const maxWidthClass = (config.position === 'top' || config.position === 'bottom') 
      ? 'max-w-[calc(100vw-2rem)]' 
      : '';
    
    // Add margin for optical form when positioned right
    const rightMargin = '';
    
    switch (config.position) {
      case 'top':
        return `${base} top-4 left-1/2 -translate-x-1/2 ${bgTheme} ${glassEffect} rounded-2xl shadow-2xl border ${maxWidthClass}`;
      case 'bottom':
        return `${base} bottom-4 left-1/2 -translate-x-1/2 ${bgTheme} ${glassEffect} rounded-2xl shadow-2xl border ${maxWidthClass}`;
      case 'left':
        return `${base} left-4 top-1/2 -translate-y-1/2 ${bgTheme} ${glassEffect} rounded-2xl shadow-2xl border`;
      case 'right':
        return `${base} right-4 top-1/2 -translate-y-1/2 ${bgTheme} ${glassEffect} rounded-2xl shadow-2xl border ${rightMargin}`;
    }
  };

  // Layout styles
  const getLayoutStyles = () => {
    const isHorizontal = config.position === 'top' || config.position === 'bottom';
    
    if (isMobile) {
      return config.isCollapsed 
        ? "p-2" 
        : isHorizontal 
          ? "p-3 max-w-[calc(100vw-2rem)]" 
          : "p-3 max-h-[calc(100vh-2rem)]";
    }
    
    return config.isCollapsed 
      ? "p-1.5" 
      : config.isCompact 
        ? "p-2" 
        : "p-2";
  };

  // Container flex direction
  const getFlexDirection = () => {
    const isHorizontal = config.position === 'top' || config.position === 'bottom';
    return isHorizontal ? 'flex-row' : 'flex-col';
  };

  const buttonTheme = config.theme === 'dark'
    ? {
        active: 'bg-blue-600 text-white shadow-lg shadow-blue-600/30',
        inactive: 'bg-gray-800 text-gray-300 hover:bg-gray-700',
        text: 'text-gray-300',
        divider: 'bg-gray-700'
      }
    : {
        active: 'bg-blue-500 text-white shadow-lg shadow-blue-500/30',
        inactive: 'bg-gray-50 text-gray-600 hover:bg-gray-100',
        text: 'text-gray-600',
        divider: 'bg-gray-200'
      };

  const ToolButton = ({ tool, icon, label, isActive, onClick, showColorIndicator = false, hideTooltip = false }: any) => (
    <button
      onClick={onClick}
      className={`group relative ${config.isCompact ? 'p-1.5' : 'p-1.5'} rounded-xl transition-all duration-200 transform hover:scale-105 ${
        isActive ? buttonTheme.active : buttonTheme.inactive
      }`}
      title={label}
    >
      <div className={config.isCompact ? 'text-sm' : 'text-base'}>{icon}</div>
      {isActive && showColorIndicator && tool !== 'eraser' && (
        <div 
          className="absolute -bottom-1 left-1/2 -translate-x-1/2 h-1 rounded-full"
          style={{ 
            backgroundColor: currentColor,
            width: '60%',
            boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
          }}
        />
      )}
      {!isMobile && !config.isCollapsed && !config.isCompact && !hideTooltip && (
        <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">
          {label}
        </span>
      )}
    </button>
  );

  const Divider = () => {
    const isHorizontal = config.position === 'top' || config.position === 'bottom';
    return (
      <div className={`${buttonTheme.divider} ${
        isHorizontal ? 'w-px h-8 mx-2' : 'h-px w-8 my-2'
      }`} />
    );
  };

  // Create toolbar sections array for overflow management
  const toolbarSections = [
    { id: 'collapse', priority: 1, content: (
      <button
        key="collapse"
        onClick={() => setConfig(prev => ({ ...prev, isCollapsed: !prev.isCollapsed }))}
        className={`${buttonTheme.inactive} p-2 rounded-lg transition-all`}
        title={config.isCollapsed ? 'Genişlet' : 'Daralt'}
      >
        {config.isCollapsed ? <FaBars /> : <FaTimes />}
      </button>
    )},
    { id: 'divider1', priority: 2, content: <Divider key="divider1" /> },
    { id: 'tools', priority: 3, content: (
      <div key="tools" className={`flex gap-1 ${getFlexDirection()}`}>
        {tools.map(({ tool, icon, label }) => (
          <ToolButton
            key={tool}
            tool={tool}
            icon={icon}
            label={label}
            isActive={currentTool === tool}
            onClick={() => handleToolChange(tool)}
            showColorIndicator={true}
          />
        ))}
      </div>
    )},
    { id: 'divider2', priority: 6, content: <Divider key="divider2" /> },
    { id: 'quickColors', priority: 7, content: (
      <div key="quickColors" className={`flex gap-1 ${getFlexDirection()}`}>
        {quickColors.map((color, index) => (
          <button
            key={index}
            onClick={() => handleColorChange(color)}
            onContextMenu={(e) => {
              e.preventDefault();
              setColorToReplace(index);
              setShowColorCustomizer(true);
            }}
            className={`${config.isCompact ? 'w-6 h-6' : 'w-8 h-8'} rounded-lg border-2 transition-all hover:scale-110 relative group ${
              currentColor === color 
                ? 'border-blue-500 scale-110 shadow-lg' 
                : config.theme === 'dark' ? 'border-gray-600' : 'border-gray-300'
            }`}
            style={{ backgroundColor: color }}
            title={`${color} - Sağ tıkla özelleştir`}
          >
            <div className="absolute inset-0 rounded-lg bg-black opacity-0 group-hover:opacity-10" />
          </button>
        ))}
        <button
          onClick={() => setShowColorPicker(!showColorPicker)}
          className={`${config.isCompact ? 'w-6 h-6' : 'w-8 h-8'} rounded-lg border-2 border-dashed transition-all hover:scale-110 ${
            config.theme === 'dark' ? 'border-gray-600 hover:border-gray-400' : 'border-gray-400 hover:border-gray-600'
          } flex items-center justify-center`}
          title="Renk Seç"
        >
          <FaPalette className="text-xs text-gray-500" />
        </button>
      </div>
    )},
    { id: 'shapes', priority: 8, content: (
      <div key="shapes" className="relative" data-shape-dropdown>
        <button
          onClick={() => setShowShapeDropdown(!showShapeDropdown)}
          className={`${
            shapeTools.some(s => s.tool === currentTool) 
              ? buttonTheme.active 
              : buttonTheme.inactive
          } ${config.isCompact ? 'p-1.5' : 'p-1.5'} rounded-xl transition-all flex items-center gap-1`}
          title="Şekiller"
        >
          {shapeTools.find(s => s.tool === currentTool)?.icon || <FaShapes className="text-lg" />}
          <FaChevronDown className="text-xs opacity-60" />
        </button>
        {showShapeDropdown && (
          <div className={`absolute ${
            config.position === 'bottom' ? 'bottom-full mb-2' : 'top-full mt-2'
          } left-0 bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-4 z-50 min-w-[320px]`}>
          <h3 className="text-sm font-bold mb-3 text-gray-700 dark:text-gray-300 flex items-center gap-2">
            <FaShapes />
            Geometrik Şekiller
          </h3>
          
          {/* 2D Shapes */}
          <div className="mb-3">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 font-medium">2D Şekiller</p>
            <div className="grid grid-cols-6 gap-1">
              {shapeTools.filter(s => s.category === '2D').map(({ tool, icon, label }) => (
                <ToolButton
                  key={tool}
                  tool={tool}
                  icon={icon}
                  label={label}
                  isActive={currentTool === tool}
                  onClick={() => {
                    handleToolChange(tool);
                    setShowShapeDropdown(false);
                  }}
                  hideTooltip={true}
                />
              ))}
            </div>
          </div>
          
          {/* 3D Shapes */}
          <div className="mb-3">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 font-medium">3D Şekiller</p>
            <div className="grid grid-cols-6 gap-1">
              {shapeTools.filter(s => s.category === '3D').map(({ tool, icon, label }) => (
                <ToolButton
                  key={tool}
                  tool={tool}
                  icon={icon}
                  label={label}
                  isActive={currentTool === tool}
                  onClick={() => {
                    handleToolChange(tool);
                    setShowShapeDropdown(false);
                  }}
                  hideTooltip={true}
                />
              ))}
            </div>
          </div>
          
          {/* Special */}
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 font-medium">Özel Araçlar</p>
            <div className="grid grid-cols-6 gap-1">
              {shapeTools.filter(s => s.category === 'Özel').map(({ tool, icon, label }) => (
                <ToolButton
                  key={tool}
                  tool={tool}
                  icon={icon}
                  label={label}
                  isActive={currentTool === tool}
                  onClick={() => {
                    handleToolChange(tool);
                    setShowShapeDropdown(false);
                  }}
                  hideTooltip={true}
                />
              ))}
            </div>
          </div>
          </div>
        )}
      </div>
    )},
    { id: 'divider3', priority: 9, content: <Divider key="divider3" /> },
    { id: 'widthColor', priority: 10, content: (
      <div key="widthColor" className={`flex gap-2 ${getFlexDirection()}`}>
        {/* Width Selector */}
        <div className="relative group">
          <button
            className={`${buttonTheme.inactive} ${config.isCompact ? 'p-1.5' : 'p-1.5'} rounded-xl transition-all flex items-center justify-center`}
            title="Kalınlık"
          >
            <svg width="24" height="24" viewBox="0 0 24 24">
              <path
                d={`M 2 12 Q 6 ${12 - currentWidth/3} 12 12 T 22 12`}
                stroke={config.theme === 'dark' ? '#e5e7eb' : '#4b5563'}
                strokeWidth={Math.min(currentWidth, 8)}
                fill="none"
                strokeLinecap="round"
              />
            </svg>
          </button>
          <div className={`absolute ${
            config.position === 'bottom' ? 'bottom-full mb-2' : 'top-full mt-2'
          } left-1/2 -translate-x-1/2 bg-white dark:bg-gray-800 rounded-lg shadow-xl p-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 min-w-[280px]`}>
            <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 text-center">
              {currentTool === 'eraser' ? 'Silgi Kalınlığı' : 'Çizgi Kalınlığı'}
            </p>
            <div className="grid grid-cols-6 gap-2">
              {(currentTool === 'eraser' ? [4, 8, 12, 16, 24, 32, 48, 64] : widths).map(w => (
                <button
                  key={w}
                  onClick={() => handleWidthChange(w)}
                  className={`p-2 rounded flex items-center justify-center relative group ${
                    currentWidth === w ? 'bg-blue-500' : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                  title={`${w}px`}
                >
                  <svg width="32" height="20" viewBox="0 0 32 20">
                    <path
                      d={`M 2 10 Q 8 ${10 - Math.min(w/4, 8)} 16 10 T 30 10`}
                      stroke={currentWidth === w ? 'white' : (config.theme === 'dark' ? '#e5e7eb' : '#4b5563')}
                      strokeWidth={Math.min(w, 10)}
                      fill="none"
                      strokeLinecap="round"
                    />
                  </svg>
                  <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                    {w}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Color Picker */}
        <button
          onClick={() => setShowColorPicker(!showColorPicker)}
          className={`${buttonTheme.inactive} ${config.isCompact ? 'p-1.5' : 'p-1.5'} rounded-xl transition-all relative`}
          title="Renk"
        >
          <FaPalette />
          <div 
            className="absolute -top-1 -right-1 w-4 h-4 rounded-full border-2 border-white dark:border-gray-900"
            style={{ backgroundColor: currentColor }}
          />
        </button>
      </div>
    )},
    { id: 'divider4', priority: 11, content: <Divider key="divider4" /> },
    { id: 'actions', priority: 12, content: (
      <div key="actions" className={`flex gap-1 ${getFlexDirection()}`}>
        <ToolButton
          icon={<FaUndo />}
          label="Geri Al"
          onClick={undoLastStroke}
        />
        <ToolButton
          icon={<FaTrash />}
          label="Temizle"
          onClick={clearCanvas}
        />
        {!showEliminationDisplay && onToggleEliminationDisplay && (
          <ToolButton
            icon={<FaTimes className="text-sm" />}
            label="Eleme Ekranını Göster"
            onClick={onToggleEliminationDisplay}
            isActive={false}
          />
        )}
        {allowExport && (
          <ExportButton
            canvas={canvasRef}
            isEnabled={true}
            className={`group relative ${config.isCompact ? 'p-1.5' : 'p-1.5'} rounded-xl transition-all duration-200 transform hover:scale-105 ${buttonTheme.inactive}`}
          />
        )}
        {hasVideo && onVideoClick && (
          <ToolButton
            icon={<FaVideo />}
            label="Çözümü İzle"
            onClick={onVideoClick}
            isActive={false}
          />
        )}
        {hasEliminatedChoices && onToggleEliminationDisplay && (
          <ToolButton
            icon={<FaTimes />}
            label={showEliminationDisplay ? "Elenenleri Gizle" : "Elenenleri Göster"}
            onClick={onToggleEliminationDisplay}
            isActive={!showEliminationDisplay}
            className={!showEliminationDisplay ? "bg-red-100 hover:bg-red-200" : ""}
          />
        )}
      </div>
    )},
    // Recording section - show if user can record or view recordings
    ...((effectivePermissions.canRecord || effectivePermissions.canViewRecordings) ? [
      { id: 'divider_recording', priority: 4, content: <Divider key="divider_recording" /> },
      { id: 'recording', priority: 5, content: (
        <div key="recording" className={`flex gap-1 ${getFlexDirection()}`}>
          {effectivePermissions.canRecord && !recordingSession.isRecording ? (
            <button
              onClick={handleStartRecording}
              className={`group relative ${config.isCompact ? 'p-1.5' : 'p-1.5'} rounded-xl bg-red-500 text-white shadow-lg hover:bg-red-600 transition-all`}
              title="Kayıt Başlat"
            >
              <FaCircle className={`${config.isCompact ? 'text-sm' : 'text-base'} animate-pulse`} />
            </button>
          ) : effectivePermissions.canRecord && recordingSession.isRecording ? (
            <>
              <div className={`flex items-center gap-2 bg-red-100 dark:bg-red-900/30 px-3 py-2 rounded-xl`}>
                <FaMicrophone className="text-red-600 dark:text-red-400 animate-pulse text-sm" />
                <span className="text-red-600 dark:text-red-400 font-medium text-xs">
                  Kayıt...
                </span>
              </div>
              <button
                onClick={handleStopRecording}
                className={`group relative ${config.isCompact ? 'p-1.5' : 'p-1.5'} rounded-xl bg-gray-800 text-white shadow-lg hover:bg-gray-900 transition-all`}
                title="Kayıt Durdur"
              >
                <FaStop className={config.isCompact ? 'text-sm' : 'text-base'} />
              </button>
            </>
          ) : null}
          
          {effectivePermissions.canViewRecordings && (
            <button
              onClick={async () => {
                setShowRecordings(!showRecordings);
                // Load recordings when button is clicked
                if (!showRecordings && currentQuestionId) {
                  try {
                    setLoadingRecordings(true);
                    // Get user info
                    const { user } = useUserStore.getState();
                    const { currentTenant } = useTenantStore.getState();
                    
                    // Load recordings based on effective permissions
                    const recordings: RecordingMetadata[] = [];
                    const recordingIds = new Set<string>();
                    
                    // Load own recordings if permitted
                    if (effectivePermissions.recordingVisibility?.showOwnRecordings && user?.id) {
                      const ownRecordings = await RecordingService.getRecordingsByQuestion(
                        currentQuestionId,
                        user.id.toString(),
                        50
                      );
                      ownRecordings.forEach(r => {
                        recordings.push(r);
                        recordingIds.add(r.id);
                      });
                    }
                    
                    // Load public recordings if permitted
                    if (effectivePermissions.recordingVisibility?.showPublicRecordings) {
                      const publicRecordings = await RecordingService.getPublicRecordingsByQuestion(
                        currentQuestionId,
                        50
                      );
                      publicRecordings.forEach(r => {
                        if (!recordingIds.has(r.id)) {
                          recordings.push(r);
                          recordingIds.add(r.id);
                        }
                      });
                    }
                    
                    // Load tenant recordings if permitted
                    if (effectivePermissions.recordingVisibility?.showTenantRecordings && currentTenant?.tenantId) {
                      const tenantRecordings = await RecordingService.getTenantRecordingsByQuestion(
                        currentQuestionId,
                        currentTenant.tenantId.toString(),
                        50
                      );
                      tenantRecordings.forEach(r => {
                        if (!recordingIds.has(r.id)) {
                          recordings.push(r);
                          recordingIds.add(r.id);
                        }
                      });
                    }
                    
                    // Sort by creation date (newest first)
                    recordings.sort((a, b) => b.createdAt - a.createdAt);
                    
                    // Update the state with filtered recordings
                    setFirebaseRecordings(recordings);
                  } catch (error) {
                    console.error('Error loading recordings:', error);
                  } finally {
                    setLoadingRecordings(false);
                  }
                }
              }}
              className={`group relative ${config.isCompact ? 'p-1.5' : 'p-1.5'} rounded-xl bg-purple-500 text-white shadow-lg hover:bg-purple-600 transition-all`}
              title="Kayıtlar"
            >
              <FaList className={config.isCompact ? 'text-sm' : 'text-base'} />
              {questionsWithRecordingsCount > 0 && (
                <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                  <span className="text-xs text-white font-bold">{questionsWithRecordingsCount}</span>
                </div>
              )}
            </button>
          )}
        </div>
      )},
      { id: 'divider6', priority: 15, content: <Divider key="divider6" /> },
    ] : []),
    // ...(allowSolutionViewing && currentQuestionId ? [
    //   { id: 'divider_solutions', priority: 14, content: <Divider key="divider_solutions" /> },
    //   { id: 'solutions', priority: 15, content: (
    //     <div key="solutions" className={`flex gap-1 ${getFlexDirection()}`}>
    //       <SolutionViewerDropdown
    //         questionId={currentQuestionId}
    //         currentTestId={currentTestId}
    //         config={{
    //           allowSolutionViewing,
    //           visibilityMode: solutionVisibilityMode,
    //           allowOwnSolutionManagement,
    //           maxSolutionsToShow,
    //           showAuthorInfo: showSolutionAuthorInfo,
    //           showSolutionStats
    //         }}
    //         className="inline-block"
    //       />
    //     </div>
    //   )}
    // ] : []),
    { id: 'settings', priority: 18, content: (
      <div key="settings" className={`flex gap-1 ${getFlexDirection()}`}>
        {/* Pen Mode Indicator - Just a dot */}
        {isPenMode && (
          <button
            onClick={() => setPenMode(false)}
            className="p-1"
            title="Kalem Modu Aktif"
          >
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
          </button>
        )}

        {/* Position Toggle */}
        <button
          onClick={() => setShowPositionMenu(!showPositionMenu)}
          className={`${buttonTheme.inactive} ${config.isCompact ? 'p-1.5' : 'p-1.5'} rounded-xl transition-all relative`}
          title="Konum"
        >
          <FaExpandAlt />
        </button>

        {/* Theme Toggle */}
        <button
          onClick={() => setConfig(prev => ({ 
            ...prev, 
            theme: prev.theme === 'light' ? 'dark' : 'light' 
          }))}
          className={`${buttonTheme.inactive} ${config.isCompact ? 'p-1.5' : 'p-1.5'} rounded-xl transition-all`}
          title="Tema"
        >
          {config.theme === 'light' ? <FaMoon /> : <FaSun />}
        </button>

        {/* Compact Mode Toggle */}
        {!isMobile && (
          <button
            onClick={() => {
              setConfig(prev => ({ ...prev, isCompact: !prev.isCompact }));
              saveToolbarPreferences();
            }}
            className={`${buttonTheme.inactive} ${config.isCompact ? 'p-1.5' : 'p-1.5'} rounded-xl transition-all`}
            title={config.isCompact ? "Normal Mod" : "Kompakt Mod"}
          >
            {config.isCompact ? <FaExpandAlt className="text-sm" /> : <FaBars className="text-sm" />}
          </button>
        )}
        
        {/* Tablet Question Navigation Toggle */}
        {isTablet && hasQuestions && onToggleTabletQuestionNav && (
          <button
            onClick={onToggleTabletQuestionNav}
            className={`${buttonTheme.inactive} ${config.isCompact ? 'p-1.5' : 'p-1.5'} rounded-xl transition-all relative`}
            title={showTabletQuestionNav ? "Soru Navigasyonunu Gizle" : "Soru Navigasyonunu Göster"}
          >
            <FaList className={config.isCompact ? 'text-xs' : 'text-sm'} />
            {totalQuestions > 0 && (
              <div className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                {currentQuestionIndex + 1}
              </div>
            )}
          </button>
        )}
        
        {/* Question List Button - Show only when not in optical mode */}
        {!isOpticalMode && questions.length > 0 && onQuestionNavigate && (
          <button
            onClick={() => setShowQuestionList(!showQuestionList)}
            className={`${buttonTheme.inactive} ${config.isCompact ? 'p-1.5' : 'p-1.5'} rounded-xl transition-all relative`}
            title="Soru Listesi"
          >
            <FaList className={config.isCompact ? 'text-xs' : 'text-sm'} />
            {totalQuestions > 0 && (
              <div className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                {currentQuestionIndex + 1}
              </div>
            )}
          </button>
        )}

        {/* Settings */}
        <button
          onClick={() => setShowSettings(!showSettings)}
          className={`${buttonTheme.inactive} ${config.isCompact ? 'p-1.5' : 'p-1.5'} rounded-xl transition-all`}
          title="Ayarlar"
        >
          <FaCog />
        </button>
      </div>
    )}
  ];

  // Sort sections by priority
  const sortedSections = toolbarSections.sort((a, b) => a.priority - b.priority);

  // Filter sections based on visible items when not collapsed
  const visibleSections = !config.isCollapsed 
    ? sortedSections.slice(0, Math.min(visibleItems, sortedSections.length))
    : sortedSections.slice(0, 1); // Only show collapse button when collapsed

  const hiddenSections = !config.isCollapsed && visibleItems < sortedSections.length
    ? sortedSections.slice(visibleItems)
    : [];

  return (
    <>
      {/* Main Toolbar */}
      <div 
        ref={toolbarRef} 
        className={`${getPositionStyles()} ${getLayoutStyles()} ${config.isDragging ? 'cursor-move' : ''}`}
        style={config.customPosition ? {
          left: `${config.customPosition.x}px`,
          top: `${config.customPosition.y}px`,
          transform: 'none'
        } : undefined}
      >
        <div ref={toolbarContentRef} className={`flex items-center gap-1 ${getFlexDirection()}`}>
          {/* Drag Handle - Only show on desktop */}
          {!isMobile && !config.isCollapsed && (
            <div
              {...dragHandleProps}
              className={`${config.isCompact ? 'p-1' : 'p-2'} cursor-move opacity-50 hover:opacity-100 transition-opacity touch-none`}
              title="Taşı"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <circle cx="4" cy="4" r="1.5" />
                <circle cx="12" cy="4" r="1.5" />
                <circle cx="4" cy="8" r="1.5" />
                <circle cx="12" cy="8" r="1.5" />
                <circle cx="4" cy="12" r="1.5" />
                <circle cx="12" cy="12" r="1.5" />
              </svg>
            </div>
          )}
          
          {/* Render visible sections */}
          {visibleSections.map(section => section.content)}
          
          {/* More button when items are hidden */}
          {hiddenSections.length > 0 && !config.isCollapsed && (
            <>
              <Divider />
              <div className="relative">
                <button
                  onClick={() => setShowMoreMenu(!showMoreMenu)}
                  className={`${buttonTheme.inactive} ${config.isCompact ? 'p-1.5' : 'p-1.5'} rounded-xl transition-all flex items-center gap-1`}
                  title="Daha Fazla"
                >
                  <FaEllipsisH />
                  {hiddenSections.some(s => ['tools', 'shapes'].includes(s.id) && 
                    (s.id === 'tools' ? tools.some(t => t.tool === currentTool) : 
                     shapeTools.some(t => t.tool === currentTool))) && (
                    <div className="absolute -top-1 -right-1 w-2 h-2 bg-blue-500 rounded-full" />
                  )}
                </button>
                
                {/* More menu dropdown */}
                {showMoreMenu && (
                  <div className={`absolute ${
                    config.position === 'bottom' ? 'bottom-full mb-2' : 
                    config.position === 'top' ? 'top-full mt-2' :
                    config.position === 'left' ? 'left-full ml-2 top-0' :
                    'right-full mr-2 top-0'
                  } ${
                    (config.position === 'top' || config.position === 'bottom') ? 'right-0' : ''
                  } bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-4 z-50 min-w-[240px] max-w-[90vw] max-h-[80vh] overflow-y-auto`}>
                    <h3 className="text-sm font-bold mb-3 text-gray-700 dark:text-gray-300">Daha Fazla Araç</h3>
                    <div className={`flex flex-wrap gap-2 ${
                      config.position === 'left' || config.position === 'right' ? 'flex-col' : ''
                    }`}>
                      {hiddenSections.map(section => (
                        <div key={section.id} className="contents">
                          {section.content}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Close more menu when clicking outside */}
      {showMoreMenu && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowMoreMenu(false)}
        />
      )}

      {/* Position Menu */}
      {showPositionMenu && (
        <div className={`fixed ${
          config.position === 'bottom' ? 'bottom-20' : 'top-20'
        } left-1/2 -translate-x-1/2 z-50 bg-white dark:bg-gray-800 rounded-xl shadow-xl p-4`}>
          <h3 className="text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">Toolbar Konumu</h3>
          <div className="grid grid-cols-2 gap-2">
            {(['top', 'bottom', 'left', 'right'] as Position[]).map(pos => (
              <button
                key={pos}
                onClick={() => {
                  setConfig(prev => ({ 
                    ...prev, 
                    position: pos,
                    customPosition: undefined,
                    customPositionRatio: undefined,
                    isDragging: false
                  }));
                  setShowPositionMenu(false);
                  // Save position preference
                  saveToolbarPreferences();
                }}
                className={`p-2 rounded-lg text-sm ${
                  config.position === pos 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {pos === 'top' && 'Üst'}
                {pos === 'bottom' && 'Alt'}
                {pos === 'left' && 'Sol'}
                {pos === 'right' && 'Sağ'}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Color Picker Popover */}
      {showColorPicker && (
        <div className={`fixed ${
          config.position === 'bottom' ? 'bottom-20' : 
          config.position === 'top' ? 'top-20' :
          config.position === 'left' ? 'left-20 top-1/2 -translate-y-1/2' :
          'right-20 top-1/2 -translate-y-1/2'
        } ${(config.position === 'top' || config.position === 'bottom') ? 'left-1/2 -translate-x-1/2' : ''} z-50 bg-white dark:bg-gray-800 rounded-xl shadow-xl p-4`}>
          <div className="grid grid-cols-5 gap-2 mb-3">
            {colors.map(color => (
              <button
                key={color}
                onClick={() => {
                  handleColorChange(color);
                  setShowColorPicker(false);
                }}
                className={`w-10 h-10 rounded-lg border-2 transition-all hover:scale-110 ${
                  currentColor === color 
                    ? 'border-blue-500 scale-110' 
                    : 'border-gray-300 dark:border-gray-600'
                }`}
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
          <input
            type="color"
            value={currentColor}
            onChange={(e) => handleColorChange(e.target.value)}
            className="w-full h-10 rounded cursor-pointer"
          />
        </div>
      )}

      {/* Color Customizer Modal */}
      {showColorCustomizer && colorToReplace !== null && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
          <div className={`${
            config.theme === 'dark' ? 'bg-gray-800 text-white' : 'bg-white'
          } rounded-2xl shadow-2xl p-6 w-full max-w-sm`}>
            <h3 className="text-lg font-bold mb-4 text-center">Hızlı Renk Özelleştir</h3>
            <p className="text-center mb-4 text-sm text-gray-600 dark:text-gray-400">
              {colorToReplace + 1}. hızlı rengi değiştir
            </p>
            
            <div className="mb-4">
              <div className="flex items-center justify-center gap-4 mb-4">
                <div 
                  className="w-16 h-16 rounded-lg border-2 border-gray-300"
                  style={{ backgroundColor: quickColors[colorToReplace] }}
                />
                <FaArrowRight className="text-gray-400" />
                <div 
                  className="w-16 h-16 rounded-lg border-2 border-blue-500"
                  style={{ backgroundColor: currentColor }}
                />
              </div>
            </div>

            <div className="grid grid-cols-5 gap-2 mb-4">
              {colors.map(color => (
                <button
                  key={color}
                  onClick={() => handleColorChange(color)}
                  className={`w-10 h-10 rounded-lg border-2 transition-all hover:scale-110 ${
                    currentColor === color 
                      ? 'border-blue-500 scale-110' 
                      : 'border-gray-300 dark:border-gray-600'
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
            
            <input
              type="color"
              value={currentColor}
              onChange={(e) => handleColorChange(e.target.value)}
              className="w-full h-10 rounded mb-4 cursor-pointer"
            />
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowColorCustomizer(false);
                  setColorToReplace(null);
                }}
                className="flex-1 py-2 px-4 bg-gray-200 dark:bg-gray-700 rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                İptal
              </button>
              <button
                onClick={() => {
                  const newQuickColors = [...quickColors];
                  newQuickColors[colorToReplace] = currentColor;
                  setQuickColors(newQuickColors);
                  localStorage.setItem('quickColors', JSON.stringify(newQuickColors));
                  setShowColorCustomizer(false);
                  setColorToReplace(null);
                }}
                className="flex-1 py-2 px-4 bg-blue-500 text-white rounded-xl hover:bg-blue-600"
              >
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
          <div className={`${
            config.theme === 'dark' ? 'bg-gray-800 text-white' : 'bg-white'
          } rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col`}>
            <div className="flex justify-between items-center p-6 pb-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-bold">Ayarlar</h3>
              <button
                onClick={() => setShowSettings(false)}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <FaTimes />
              </button>
            </div>

            <div className="p-6 pt-4 overflow-y-auto flex-1">
              <div className="space-y-4">
              {/* Device Info */}
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                <h4 className="font-medium text-blue-900 dark:text-blue-300 mb-3">Cihaz Bilgileri</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Cihaz ID:</span>
                    <span className="font-mono text-xs text-gray-800 dark:text-gray-200">{generateDeviceId().slice(-12)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Kullanıcı ID:</span>
                    <span className="font-medium text-gray-800 dark:text-gray-200">{useUserStore.getState().userInfo?.id || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Tenant ID:</span>
                    <span className="font-medium text-gray-800 dark:text-gray-200">{useTenantStore.getState().tenantId || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Platform:</span>
                    <span className="font-medium text-gray-800 dark:text-gray-200">{navigator.platform}</span>
                  </div>
                </div>
              </div>

              {/* Pen Mode */}
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-xl">
                <div>
                  <p className="font-medium">Kalem Modu</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Sadece kalem girişi</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isPenMode}
                    onChange={(e) => handlePenModeChange(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {/* Touch Enabled */}
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-xl">
                <div>
                  <p className="font-medium">Dokunma Desteği</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Parmakla çizim</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={touchEnabled}
                    onChange={(e) => handleTouchEnabledChange(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {/* Shape Recognition */}
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-xl">
                <div>
                  <p className="font-medium">Şekil Tanıma</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Otomatik düzeltme</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={shapeRecognitionEnabled}
                    onChange={(e) => handleShapeRecognitionChange(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {/* Answer Feedback */}
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-xl">
                <div>
                  <p className="font-medium">Cevap Kontrolü</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Doğru/yanlış işaretlerini göster</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showAnswerFeedback}
                    onChange={(e) => handleShowAnswerFeedbackChange(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {/* Pen Settings */}
              <div className="space-y-4">
                <h4 className="font-medium">Kalem Ayarları</h4>
                
                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-xl">
                  <label className="block text-sm font-medium mb-2">
                    Hassasiyet: <span className="text-blue-600">{penSettings.sensitivity.toFixed(1)}</span>
                  </label>
                  <input
                    type="range"
                    min="0.1"
                    max="2"
                    step="0.1"
                    value={penSettings.sensitivity}
                    onChange={(e) => handlePenSettingsChange({ sensitivity: parseFloat(e.target.value) })}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                  />
                </div>

                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-xl">
                  <label className="block text-sm font-medium mb-2">
                    Yumuşatma: <span className="text-blue-600">{penSettings.smoothing.toFixed(1)}</span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={penSettings.smoothing}
                    onChange={(e) => handlePenSettingsChange({ smoothing: parseFloat(e.target.value) })}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-xl">
                  <label className="text-sm font-medium">Basınç Hassasiyeti</label>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={penSettings.pressureSensitivity}
                      onChange={(e) => handlePenSettingsChange({ pressureSensitivity: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>

              {/* Glass Effects */}
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-xl">
                <div>
                  <p className="font-medium">Cam Efektleri</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Şeffaf arka plan efektleri</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={glassEffects !== false}
                    onChange={(e) => handleGlassEffectsChange(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {/* Glass Color Picker - Only show when glass effects are enabled */}
              {glassEffects !== false && (
                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-xl">
                  <p className="font-medium mb-3">Cam Rengi</p>
                  <div className="grid grid-cols-5 gap-2">
                    <button
                      onClick={() => handleGlassColorChange('crystal')}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        (devicePreferences?.preferences?.glassColor || 'crystal') === 'crystal'
                          ? 'border-blue-500 shadow-lg'
                          : 'border-transparent'
                      }`}
                      title="Kristal Berrak"
                    >
                      <div className="w-full h-8 rounded bg-gradient-to-br from-white/80 to-gray-200/80 backdrop-blur-sm" />
                    </button>
                    <button
                      onClick={() => handleGlassColorChange('ocean')}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        devicePreferences?.preferences?.glassColor === 'ocean'
                          ? 'border-blue-500 shadow-lg'
                          : 'border-transparent'
                      }`}
                      title="Okyanus Mavisi"
                    >
                      <div className="w-full h-8 rounded bg-gradient-to-br from-blue-400/80 to-blue-600/80 backdrop-blur-sm" />
                    </button>
                    <button
                      onClick={() => handleGlassColorChange('sunset')}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        devicePreferences?.preferences?.glassColor === 'sunset'
                          ? 'border-blue-500 shadow-lg'
                          : 'border-transparent'
                      }`}
                      title="Gün Batımı"
                    >
                      <div className="w-full h-8 rounded bg-gradient-to-br from-orange-400/80 to-red-500/80 backdrop-blur-sm" />
                    </button>
                    <button
                      onClick={() => handleGlassColorChange('forest')}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        devicePreferences?.preferences?.glassColor === 'forest'
                          ? 'border-blue-500 shadow-lg'
                          : 'border-transparent'
                      }`}
                      title="Orman Yeşili"
                    >
                      <div className="w-full h-8 rounded bg-gradient-to-br from-green-400/80 to-green-600/80 backdrop-blur-sm" />
                    </button>
                    <button
                      onClick={() => handleGlassColorChange('royal')}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        devicePreferences?.preferences?.glassColor === 'royal'
                          ? 'border-blue-500 shadow-lg'
                          : 'border-transparent'
                      }`}
                      title="Kraliyet Moru"
                    >
                      <div className="w-full h-8 rounded bg-gradient-to-br from-purple-400/80 to-purple-600/80 backdrop-blur-sm" />
                    </button>
                  </div>
                </div>
              )}

              {/* Question Frame Margins */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900 dark:text-gray-100">Soru Çerçeve Kenar Boşlukları</h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">Soruların ekran kenarlarından uzaklığını ayarlayın</p>
                
                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-xl space-y-3">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                      Üst Boşluk: <span className="text-blue-600 dark:text-blue-400">{devicePreferences?.preferences?.questionFrameMargins?.top || 50}px</span>
                    </label>
                    <input
                      type="range"
                      min="25"
                      max="100"
                      value={devicePreferences?.preferences?.questionFrameMargins?.top || 50}
                      onChange={(e) => handleQuestionFrameMarginsChange({ top: parseInt(e.target.value) })}
                      className="w-full h-2 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer slider"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                      Alt Boşluk: <span className="text-blue-600 dark:text-blue-400">{devicePreferences?.preferences?.questionFrameMargins?.bottom || 250}px</span>
                    </label>
                    <input
                      type="range"
                      min="50"
                      max="500"
                      value={devicePreferences?.preferences?.questionFrameMargins?.bottom || 250}
                      onChange={(e) => handleQuestionFrameMarginsChange({ bottom: parseInt(e.target.value) })}
                      className="w-full h-2 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer slider"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                      Sol Boşluk: <span className="text-blue-600 dark:text-blue-400">{devicePreferences?.preferences?.questionFrameMargins?.left || 30}px</span>
                    </label>
                    <input
                      type="range"
                      min="10"
                      max="100"
                      value={devicePreferences?.preferences?.questionFrameMargins?.left || 30}
                      onChange={(e) => handleQuestionFrameMarginsChange({ left: parseInt(e.target.value) })}
                      className="w-full h-2 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer slider"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                      Sağ Boşluk: <span className="text-blue-600 dark:text-blue-400">{devicePreferences?.preferences?.questionFrameMargins?.right || 30}px</span>
                    </label>
                    <input
                      type="range"
                      min="10"
                      max="100"
                      value={devicePreferences?.preferences?.questionFrameMargins?.right || 30}
                      onChange={(e) => handleQuestionFrameMarginsChange({ right: parseInt(e.target.value) })}
                      className="w-full h-2 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer slider"
                    />
                  </div>

                  <button
                    onClick={() => handleQuestionFrameMarginsChange({ top: 50, bottom: 250, left: 30, right: 30 })}
                    className="w-full mt-3 py-2 px-4 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 text-sm text-gray-700 dark:text-gray-200"
                  >
                    Varsayılan Değerlere Dön
                  </button>
                </div>
              </div>
            </div>
          </div>
      </div>
      </div>
      )}

      {/* Save Recording Dialog */}
      {showSaveDialog && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
          <div className={`${
            config.theme === 'dark' ? 'bg-gray-800 text-white' : 'bg-white'
          } rounded-2xl shadow-2xl p-6 w-full max-w-sm`}>
            <h3 className="text-xl font-bold mb-4 text-center">Kayıt Tamamlandı!</h3>
            <p className="text-center mb-4 text-gray-600 dark:text-gray-400">
              Kayıtınıza bir isim verin
            </p>
            
            <input
              type="text"
              value={recordingName}
              onChange={(e) => setRecordingName(e.target.value)}
              className={`w-full px-4 py-3 rounded-xl border-2 ${
                config.theme === 'dark' 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-200'
              } focus:border-blue-500 focus:outline-none`}
              placeholder="Kayıt adı..."
              autoFocus
            />
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowSaveDialog(false)}
                className="flex-1 py-2 px-4 bg-gray-200 dark:bg-gray-700 rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                İptal
              </button>
              <button
                onClick={handleSaveRecording}
                className="flex-1 py-2 px-4 bg-blue-500 text-white rounded-xl hover:bg-blue-600 flex items-center justify-center gap-2"
              >
                <FaSave />
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recordings List Modal */}
      {showRecordings && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
          <div className={`${
            config.theme === 'dark' ? 'bg-gray-800 text-white' : 'bg-white'
          } rounded-2xl shadow-2xl p-6 w-full max-w-2xl max-h-[80vh] overflow-hidden`}>
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-xl font-bold">
                  {currentQbQuestion?.questionNo ? `Soru ${currentQbQuestion.questionNo} - ` : ''}
                  Kayıtlar
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {firebaseRecordings.length} kayıt bulundu
                  {firebaseRecordings.some(r => r.solutionMetadata?.isPublic) && ' (genel kayıtlar dahil)'}
                </p>
              </div>
              <button
                onClick={() => setShowRecordings(false)}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <FaTimes />
              </button>
            </div>

            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              {loadingRecordings ? (
                <div className="text-center py-12">
                  <FaSpinner className="text-4xl mx-auto mb-4 animate-spin text-blue-500" />
                  <p className="text-gray-500 dark:text-gray-400">Kayıtlar yükleniyor...</p>
                </div>
              ) : (firebaseRecordings.length === 0) ? (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  <FaCircle className="text-4xl mx-auto mb-4 opacity-50" />
                  <p>Henüz kayıt bulunmuyor</p>
                  <p className="text-sm">İlk kayıtınızı oluşturun!</p>
                </div>
              ) : (
                <>
                  {firebaseRecordings.map((recording: RecordingMetadata) => (
                    <div 
                      key={recording.id} 
                      className={`${
                        config.theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
                      } rounded-xl p-4 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors mb-3`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold mb-1">
                            {recording.solutionMetadata?.solutionName || 
                             `Çözüm ${recording.solutionMetadata?.solutionNumber || 1}`}
                          </h4>
                          <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                            <span>
                              {recording.authorMetadata.name}
                              {recording.solutionMetadata?.isPublic && ' (Genel)'}
                            </span>
                            <span>{new Date(recording.createdAt).toLocaleDateString('tr-TR')}</span>
                            {recording.duration && <span>{formatDuration(recording.duration * 1000)}</span>}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                             
                              setShowRecordings(false); // Close modal first
                              setTimeout(() => {
                                navigate(`/player/${recording.id}`);
                              }, 100); // Small delay to ensure modal closes
                            }}
                            className="p-2 rounded-lg bg-green-500 text-white hover:bg-green-600"
                            title="Oynat"
                          >
                            <FaPlay />
                          </button>
                          <button
                            onClick={() => handleShareRecording({ id: recording.id } as Recording)}
                            className="p-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600"
                            title="Paylaş"
                          >
                            <FaShare />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Question List Panel */}
      {showQuestionList && onQuestionNavigate && (
        <QuestionListPanel
          questions={questions}
          currentQuestionIndex={currentQuestionIndex}
          onQuestionNavigate={onQuestionNavigate}
          onClose={() => setShowQuestionList(false)}
          groups={qbGroups}
        />
      )}

      <style dangerouslySetInnerHTML={{__html: `
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}} />
    </>
  );
};

export default ModernToolbar;